// i18n area 'storage' (task #127): sections storage.
// Split out of the old monolithic dictionaries.ts by scripts/split-dictionaries.mjs;
// since the split, these files are the SOURCE — add and edit keys here. The script
// only re-runs to re-split a merged monolithic dictionaries.ts (see its header).

export const enUS = {
  storage: {
    pools: {
      title: 'Storage',
      createTitle: 'Create storage',
      path: 'Path',
      pathHint: 'Absolute path under an allowlisted root (default /var/lib/tvirt/pools/)',
      shared: 'Shared storage',
      sharedChip: 'shared',
      sharedVerifiedChip: 'shared · verified',
      sharedVerifiedTooltip: 'Sharing verified: cross-node probes confirm every member sees the data the others write.',
      sharedDisputedChip: 'shared · NOT verified',
      sharedDisputedTooltip: 'Sharing NOT verified',
      sharedDisputedPair: '{observer} does not see the data written by {writer}',
      sharedHint: 'A network share mounted locally at the same path on every member — the live-migration prerequisite (declarative).',
      sharedDanger: 'DANGER — TVirt BELIEVES this flag: turn it on and every member assumes it sees the SAME bytes at this path. On a directory that is NOT a real shared mount (NFS/CIFS/cluster FS) the result is data corruption and loss the first time a VM migrates or HA restarts it elsewhere. Only enable it if the path is a share mounted identically on every node.',
      type: 'Type',
      capacity: 'Capacity',
      provisioned: 'Provisioned',
      provisionedOvercommit: 'Potential commitment beyond capacity: the thin volumes can grow up to {n} GiB.',
      phase: { ready: 'Ready', degraded: 'Degraded', error: 'Error' },

      // Round #156 (dialog standard): the COMPLETE details dialog — tabs,
      // the per-driver config rows and the observed surface.
      tabConfig: 'Configuration',
      tabCapacity: 'Capacity and state',
      cardIdentity: 'Identity',
      cardPlacement: 'Placement',
      cardConnection: 'Connection',
      usedReal: 'Real usage',
      capacityUnknown: 'The node has not reported this pool capacity yet — no numbers are invented here',
      overcommitHint: 'Provisioned above capacity: legitimate on thin pools, watch the real usage',
      supportedContent: 'Supported content',
      content: {
        disks: 'Disks',
        catalogFiles: 'Catalog files',
        backups: 'Backups',
        appVolumes: 'App volumes'
      },
      defaultDiskFormat: 'Default disk format',
      reserveGiB: 'Reserved free space (GiB)',
      available: 'Available',
      diskCount: 'Disks',
      filesystem: 'Filesystem',
      observedAt: 'Observed at',
      lostAtRow: 'Declared lost at',
      sharedProbeRow: 'Sharing verification',
      sharedProbeNodes: 'Probed nodes',
      reachableFrom: 'Declared reachability',
      reachableFromHint: 'The nodes this storage must be reachable from. A VM whose disks, ISOs or metadata live here will not start or migrate to a node outside this list. All members are preselected — narrow it as you wish. A node that joins the cluster later stays out until you add it here.',
      reachableFromNone: 'not declared',
      reachableFromTags: 'Reachability by tag',
      reachableFromTagsHint: 'tag=value selector (comma-separated entries, ANDed): every member carrying these tags is declared reachable — dynamically, tagging a node brings it in.',
      reachableFromTagsInvalid: 'Invalid format — use tag=value, comma-separated',
      reachableResolved: 'Resolved nodes: {nodes}',
      reachableDeclaredNotSeen: 'declared but not seen by the sharing probe on: {nodes}',
      reachableSeenNotDeclared: 'seen but not declared (outside the perimeter) on: {nodes}',
      cephCluster: 'Managed Ceph cluster',
      appGuardTitle: 'Storage in use by Apps',
      appEphemeralGuard: 'this storage is the cluster ephemeral pool of the Apps — change it from the Apps page first, then delete it.',
      appInUseGuard: 'app roots/volumes live here: {apps} — move or delete them first.',

      // #355 — il rifiuto `release_required` della delete: il pool OSPITA
      // immagini OCI materializzate, e la conferma le fa tornare non
      // materializzate senza cancellare un byte (idioma `purge_required`).
      releaseRequiredTitle: "Delete '{name}' and release its images?",
      releaseRequiredBatchTitle: 'Delete the selected storages and release their images?',
      releaseRequiredBody: 'Releasing the images deletes NO bytes: unregistering leaves the namespace on disk, the images simply stop being materialized and every scope reference survives as a claim — register the pool again and they are re-adopted.',
      releaseConfirm: 'Delete and release the images'
    },
    lost: {
      chip: 'Lost',
      action: 'Declare lost',
      confirmTitle: 'Declare storage lost',
      confirmBody: 'Operator attestation at your own declared risk: every queued or running task targeting this storage or its objects will FAIL, deletes become record-only (no filesystem work) and new physical work is refused. No bytes are ever touched — if the backing comes back to life, register it again and run a scan to re-adopt everything.',
      confirmSubmit: 'Declare lost',
      error: 'The attestation was refused'
    },
    findings: {
      action: 'Found objects',
      title: 'Found objects',
      empty: 'Nothing found in the storage namespace',
      loadError: 'The findings could not be loaded',
      actionError: 'The decision was refused',
      colKind: 'Type',
      colObject: 'Object',
      colConflict: 'Conflict',
      namespaceNotice:
        "This directory was already initialized by another storage: the identity seal has been rewritten with this storage's. The contents were NOT touched — review the objects found below, import the ones you need, then dismiss this notice.",
      kind: {
        disk: 'Disk',
        catalogFile: 'Catalog file',
        vmMetadata: 'VM metadata',
        namespace: 'Namespace',
        foreign: 'Foreign',
        volumeGroup: 'Volume group'
      },
      vgAdopt: {
        action: 'Adopt the volume group',
        title: 'Adopt the volume group',
        intro:
          'This device already carries a volume group named the way TVirt names its own, but its seal names no storage registered here. It may be what a previous storage of THIS cluster left on the LUN — or what ANOTHER TVirt cluster left on a LUN that changed hands: from here the two look identical, which is why nobody decides for you.',
        irreversible: 'Look at the device, the seal and the name before you go on: the adoption cannot be undone.',
        device: 'Device',
        seal: 'Seal',
        sealNone: 'none (no tvirt.storageId tag)',
        renameTo: 'It will be renamed to',
        renameNone: 'No rename: the name is already the one this storage derives',
        volumesNote:
          'The volumes inside are NOT adopted by this gesture: the next scan offers them one by one, as ordinary found objects.'
      },
      conflictChip: 'ID already exists',
      dismissedChip: 'Ignored',
      adopt: 'Adopt',
      adoptFileTitle: 'Import into a catalog',
      adoptFileHint: 'Files exist only inside catalogs: pick the destination catalog for the found file.',
      purge: 'Delete from disk',
      dismiss: 'Ignore',
      purgeConfirmTitle: 'Delete from disk',
      purgeConfirmBody: 'The found bytes of {id} will be removed from the storage. This cannot be undone.',
      taskStarted: 'Started — it continues as a background task',
      scan: 'Scan storage',
      scanStarted: 'Scan started…',
      scanDone: 'Scan finished — the findings are up to date'
    },
    reinit: {
      action: 'Retry initialization'
    },
    wizard: {
      addButton: 'Add',
      title: 'Add storage',
      back: 'Back',
      continue: 'Continue',
      selectHint: 'Select a protocol to see how TVirt uses it.',
      roadmap: 'Roadmap',
      server: 'Server',
      export: 'Exported path',
      share: 'Share',
      username: 'Username',
      password: 'Password',
      passwordHint: 'Write-only: stored for the mount, never shown again.',
      credential: 'Credential',
      credentialManual: 'Enter manually',
      credentialHint: 'A keystore entry of kind username and password — the alternative to typing them here.',
      credentialEmpty: 'No username-and-password credential in the keystore yet — create one under System → Credentials, or type the account inline below.',
      options: 'Mount options',
      optionsHint: 'Comma-separated, passed to mount -o verbatim.',
      portal: 'Portal',
      portalHint: 'host[:port] — port defaults to 3260.',
      target: 'Target IQN',
      targetHint: 'The iSCSI target name, starting with iqn.',
      lun: 'LUN',
      wholeLunRow: 'On the disks: this storage names the target only',
      wholeLunHint: 'This storage names the target only: its LUNs become disks one by one (Create disk → adopt a whole LUN), nothing is written on the target.',
      lunHint: 'LUN number on the target — 0 if in doubt.',
      chapCredential: 'Credential (CHAP)',
      chapCredentialHint: 'A keystore entry of kind CHAP (iSCSI) used for the login — the secret never rides the storage.',
      chapNone: 'No authentication',
      chapEmpty: 'No CHAP (iSCSI) credential in the keystore yet — create one under System → Credentials. Without one the login stays unauthenticated.',
      address: 'Address',
      addressHint: 'Host or IP of the NVMe/TCP target (traddr).',
      port: 'Port',
      portHint: 'TCP port of the target — 4420 if left empty.',
      targetNqn: 'Target NQN',
      targetNqnHint: 'The subsystem NQN on the target, starting with nqn.',
      nsid: 'NSID',
      nsidHint: 'Namespace ID inside the subsystem — 1 if in doubt.',
      dhchapCredential: 'Credential (DH-HMAC-CHAP)',
      dhchapCredentialHint: 'A keystore entry of kind DH-HMAC-CHAP (NVMe) used at connect time — the secret never rides the storage.',
      dhchapNone: 'No authentication',
      dhchapEmpty: 'No DH-HMAC-CHAP (NVMe) credential in the keystore yet — create one under System → Credentials. Without one the connection stays unauthenticated.',
      monitors: 'Monitors',
      monitorsHint: 'Ceph monitor endpoints, host[:port], comma or newline separated — port defaults to 6789.',
      pool: 'Pool',
      poolHint: 'The RBD pool to consume — one RBD image per disk.',
      fs: 'Filesystem',
      fsHint: 'The CephFS filesystem name (mds_namespace at mount time).',
      fsPath: 'Path inside the filesystem',
      fsPathHint: 'Absolute path inside the filesystem the storage mounts — / if in doubt.',
      cephxCredential: 'Credential (cephx)',
      cephxCredentialHint: 'A keystore entry of kind cephx: username = client name (client.…), secret = the base64 key. Required.',
      cephxNone: 'No cephx credential in the keystore yet — create one (kind: cephx) under System → Credentials, or let a managed Ceph cluster mint it via "Propose as storage".',
      sharedByNature: 'Network storages are shared by nature: every member mounts them at the same TVirt-managed path.',
      error: 'The storage was refused',
      category: {
        nas: 'NAS',
        san: 'SAN',
        ceph: 'Ceph',
        other: 'Other',
        legacy: 'Legacy'
      },
      legacyHint: 'Still fully supported — we just would not pick these for a new installation.',
      proto: {
        // The `usage` line (Luca 2026-07-24) says how TVirt uses the protocol —
        // the accepted contents above all: dir/nfs/cifs host disks AND ISO/disk
        // images and templates, the block drivers (iscsi/nvme-tcp/rbd) hold
        // disks ONLY.
        dir: {
          name: 'Local folder',
          desc: 'A directory on the node: simple and fast. It is LOCAL to one node unless the path is a real shared mount — marking it shared when it is not costs data.',
          usage: 'TVirt stores VM disks, ISO/disk images and templates in it.'
        },
        nfs: {
          name: 'NFS',
          desc: 'An NFS export mounted by the cluster — shared by nature.',
          usage: 'Holds VM disks, ISO/disk images and templates.'
        },
        cifs: {
          name: 'CIFS/SMB',
          desc: 'A Windows/Samba share mounted by the cluster — shared by nature.',
          usage: 'Holds VM disks, ISO/disk images and templates.'
        },
        sftp: {
          name: 'SFTP',
          desc: 'A remote filesystem over SSH — no NAS export needed.',
          usage: 'Roadmap.'
        },
        iscsi: {
          name: 'iSCSI',
          desc: 'Block LUNs over IP with shared LVM on top.',
          usage: 'Disks only — one thin volume per disk; it does not host ISO images or templates.'
        },
        rbd: {
          name: 'Ceph RBD',
          desc: 'Distributed block devices on a Ceph cluster.',
          usage: 'Disks only — one RBD image per disk, opened natively by QEMU; it does not host ISO images.'
        },
        cephfs: {
          name: 'CephFS',
          desc: 'The Ceph distributed filesystem, kernel-mounted by the cluster — shared by nature.',
          usage: 'Holds VM disks, ISO/disk images and templates — the dir family on a Ceph filesystem (cephx credential required).'
        },
        fc: { name: 'SCSI/FC', desc: 'SCSI LUNs over a FibreChannel fabric — the classic FC SAN.' },
        fcoe: { name: 'SCSI/FCoE', desc: 'The same SCSI-over-FibreChannel, with the FC frames carried inside Ethernet.' },

        // Live since round #132 — the key is the contract driver name, so the
        // storage-list chip resolves the same entry.
        // Round #173 (T173-P10): the whole-LUN carriers — the key is the contract
        // driver name, so the storage-list chip resolves the same entry.
        'iscsi-lun': {
          name: 'iSCSI (whole LUNs)',
          desc: 'The iSCSI target as a carrier of whole LUNs: each disk IS a LUN, passed raw to the guest — no LVM in between.',
          usage: 'Disks only — one whole LUN per disk, adopted as is; the SAN home of shared-write disks.'
        },
        'nvme-tcp-lun': {
          name: 'NVMe/TCP (whole namespaces)',
          desc: 'The NVMe subsystem as a carrier of whole namespaces: each disk IS a namespace, passed raw to the guest.',
          usage: 'Disks only — one whole namespace per disk, adopted as is; shared write yes, SCSI persistent reservations no.'
        },
        'nvme-tcp': {
          name: 'NVMe/TCP',
          desc: 'Remote NVMe — no SCSI in the path — over plain TCP, with shared LVM on top.',
          usage: 'Disks only — one thin volume per disk; it does not host ISO images or templates.'
        },
        nvmefc: { name: 'NVMe/FC', desc: 'Remote NVMe over a FibreChannel fabric.' },
        nvmefcoe: { name: 'NVMe/FCoE', desc: 'NVMe over the FibreChannel fabric that FCoE carries inside Ethernet — the FCoE and NVMe/FC layers stacked.' },
        iser: { name: 'iSCSI/RDMA (iSER)', desc: 'iSCSI with the data path moved onto RDMA — any fabric (InfiniBand, iWARP, RoCE). Superseded by NVMe/RDMA, still shipped by arrays and by ESXi.' },
        srp: { name: 'SCSI/RDMA (SRP)', desc: 'Native SCSI over RDMA, the pre-iSER era: InfiniBand (with fabric discovery) and RoCE — survives in dated HPC storage.' },
        nvmerdma: { name: 'NVMe/RDMA', desc: 'Remote NVMe over any RDMA fabric — InfiniBand, iWARP, RoCE v1 and v2: one setup, the node reports which one it has.' },
        s3: { name: 'S3', desc: 'S3-compatible object storage — images and backups.' }
      }
    },
    catalogs: {
      title: 'Catalogs',
      createTitle: 'Create catalog',
      editTitle: 'Edit catalog',
      docTitle: 'Catalog',
      notFound: 'This catalog does not exist (anymore?)',
      scopeHint: 'Whoever reads this scope sees (and can use) every file in the catalog',
      files: 'Files',
      noFiles: 'No files',
      count: {
        iso: 'ISO',
        ctRootfs: 'CT tpl',
        vmTemplate: 'VM tpl',
        ociImage: 'OCI',
        firmwareOvmf: 'Firmware'
      },

      // The system catalog (round #126): the home of the platform's embedded
      // objects — readable by everyone, writable by no one.
      systemChip: 'system',
      systemTooltip: 'System catalog: it hosts the platform\'s embedded objects and cannot be changed'
    },

    // Catalog files (round K1, inventory v2e/v2g): the catalog is the home of
    // the cluster's inert file artefacts — one list component, per-kind titles.
    catalogFiles: {
      catalog: 'Catalog',
      kindLabel: 'Kind',

      // #264: both sizes of a disk-shaped file — the bytes it occupies on the
      // storage and the capacity the guest sees (multi-disk packages: the
      // sum of their member disks).
      physicalSize: 'Physical size',
      virtualSize: 'Virtual size',

      // Round K3 (#64) + K4 (#65, ova/ovf via the boot-survival mapping):
      // materialize a vm-template file as an inventory VM Template.
      importTemplateAction: 'Import as VM Template',
      importTemplateLegacy: 'No declared format — usable as a disk seed, not importable',
      importTemplateTitle: 'Import as VM Template',
      importTemplateSubmit: 'Import',
      importTemplateError: 'Import failed',
      importTemplateHint: 'The .tvtpl package becomes a VM Template: the manifest’s frozen hardware plus one disk per archive entry, each verified against its checksum. NICs and CD/DVD media are not part of an archive — add them after the import.',
      importTemplateQcow2Hint: 'A naked qcow2 becomes a ONE-disk template with the declared default hardware: linux profile, UEFI, 2 vCPU, 2048 MiB, boot from its only disk. Adjust at deploy time.',
      importTemplateName: 'Template name',
      importTemplateNameHint: 'Blank = {name} (-import-<n> when taken)',
      importTemplateStorage: 'Storage for the disks',
      importTemplateMetadataStorage: 'Metadata storage',
      importTemplateMetadataHint: 'A block storage holds no VM metadata (firmware, varstore) — pick a dir/nfs/cifs one.',
      importTemplateStarted: 'Import started — the template appears in the Templates tab when the task completes.',

      // Singular, speaking labels (#86): storage findings and the census say
      // WHAT the file is — the generic 'Catalog file' stays as fallback only.
      kind: {
        iso: 'ISO image',
        'ct-rootfs': 'CT rootfs',
        'vm-template': 'VM template',
        'oci-image': 'OCI image',
        'firmware-ovmf': 'OVMF firmware'
      },
      title: {
        iso: 'ISO images',
        'ct-rootfs': 'CT templates',
        'vm-template': 'VM templates',
        'oci-image': 'OCI images',
        'firmware-ovmf': 'OVMF firmware'
      },
      createTitle: {
        iso: 'Add ISO image',
        'ct-rootfs': 'Register CT template',
        'vm-template': 'Add VM template',
        'oci-image': 'Add OCI image',
        'firmware-ovmf': 'Add OVMF firmware'
      },
      formatHint: 'Derived from the chosen file\'s extension — adjust if it guessed wrong',
      tarballHint: 'A rootfs tarball in the standard LXC format (.tar.gz / .tar.xz / .tar.zst / .tar.bz2, or a plain .tar)',
      ociArchiveHint: 'A docker-archive / oci-archive tarball (.tar or .tar.gz), e.g. from docker save',
      uploadResumeHint: 'Upload interrupted — the file is awaiting upload: submit again with the same file to resume',
      moveTitle: 'Move file',
      moveHint: 'Two independent dimensions: Catalog moves the link (same id, no byte moves, the scope becomes the target catalog\'s); Storage moves the data — every linked copy follows, refused while the file is in use. Chosen together, catalog first, then storage.',
      moveCatalog: 'Target catalog',
      moveCatalogHint: 'Only the link changes home: a VM mounting the file must be able to see the target catalog',
      keepCurrent: 'Keep as is',
      rehomed: 'Link moved into the chosen catalog',
      copyAction: 'Copy into a catalog…',
      copyTitle: 'Copy into a catalog',
      copyLinkHint: 'The copy is a new link to the same data — nothing is duplicated on the storage.',
      copyCatalog: 'Target catalog',
      copyCatalogHint: 'Copying is how a file travels between catalogs',
      sameCatalog: 'Same as the source',
      sharedObjectHint: 'This file\'s data is shared by {count} linked copies',
      deleteLastLink: 'This is the LAST link to this content: the data will be deleted from the storage too.',
      deleteLinkOnly: 'Only this link will be removed — the data stays, still reachable through the other linked copies ({count}).',
      noCatalog: 'There is no catalog yet — create one',
      inlineCatalogName: 'Catalog name',

      // Embedded platform files (round #126): storage null — read and clone,
      // never move/delete/rename.
      embeddedChip: 'embedded',
      embeddedStorage: 'Embedded in the platform',
      embeddedNoMove: 'Embedded platform file: it lives on no storage — clone it instead',
      embeddedCopyHint: 'The clone materializes the embedded bytes onto a real storage: pick the target storage and catalog — the copy is an ordinary file',
      sourceEmbedded: 'Embedded in the platform',

      // Round #142 (T142-P4): the per-node matrix of an embedded file —
      // where the asset is (and which version), where it is missing.
      nodesRow: 'Per-node presence',
      nodesSummary: '{present} of {total} nodes ship it',
      nodePresent: 'present',
      nodeMissing: 'missing',
      nodeUnknown: 'not reported yet',
      nodesHint: 'The asset lives on each node\'s disk (package trantor-tvirt-assets): a node without it can neither define a VM with this firmware nor serve the Guest Tools. Different versions across nodes are normal during a rolling update.',

      // Download (round #108): the bytes ride their own permission.
      downloadAction: 'Download',
      downloadNotReady: 'The content is not ready yet — the download needs a ready file',
      downloadDenied: 'Your role does not include catalog.download — downloading the bytes is a separate right from using the file',

      // Round #156 (dialog standard): the missing read rows of the details
      // dialog — the shared object facts, the declared checksum, the users.
      objectRow: 'Storage object',
      objectLinksRow: 'Linked copies',
      declaredChecksum: 'Declared SHA-256',
      usedByRow: 'Used by',
      usedByVms: 'VMs',
      usedByDisks: 'Disks',
      transferRow: 'Transfer',
      storageLockHint: 'Changes only through the Move action'
    },

    // The per-storage census (round K1, inventory v2b #2): read-only, every
    // object managed from its own section.
    objects: {
      action: 'Explore contents',
      title: 'Storage contents',
      hint: 'Read-only census of what lives on this storage — every object is managed from its own section.',
      empty: 'Nothing registered on this storage',
      loadError: 'The contents could not be loaded',
      colDetail: 'Detail',
      manage: 'Manage in its section',
      kind: {
        disk: 'Disk',
        catalogFile: 'Catalog file',
        ociBlob: 'OCI blob',
        checkpoint: 'Checkpoint',
        vmMetadata: 'VM metadata',
        appRoot: 'App root',
        appVolume: 'App volume'
      }
    },
    inventory: {
      tabCatalogs: 'Catalogs',
      storage: 'Storage',
      sizeGib: 'Size (GiB)',
      format: 'Format',
      size: 'Size',
      sourceType: 'Source',
      sourceFile: 'File upload',
      sourceUrl: 'URL download',
      file: 'File',
      url: 'URL',
      checksum: 'SHA-256 checksum',
      checksumHint: 'Required: the download is verified against this SHA-256',
      verifyChecksum: 'Verify checksum',
      checksumOffHint: 'The download will be accepted unverified — the computed SHA-256 is still recorded',
      uploading: 'Uploading…',
      move: {
        action: 'Move',
        diskTitle: 'Move disk',
        target: 'Target storage',
        diskHint: 'The disk keeps its identity: the bytes travel to the chosen storage. A VM using it must be stopped; with snapshots present an iSCSI side refuses the move.',
        noTargets: 'No compatible target storage',
        submit: 'Move',
        error: 'The move was refused'
      },
      clone: {
        action: 'Clone',
        title: 'Clone disk',
        name: 'Name of the copy',
        storage: 'Target storage',
        sameStorage: 'Same as the source',
        linked: 'Linked clone',
        linkedHint: 'Instant qcow2 overlay chained to the source disk (qcow2 sources only). Default is a full copy.',
        linkedCrossHint: 'A linked clone must stay on the source storage: pick "Same as the source" to enable it.',
        submit: 'Clone',
        error: 'The clone was refused'
      },
      copy: {
        action: 'Copy',
        name: 'Name of the copy',
        storage: 'Target storage',
        sameStorage: 'Same as the source',
        submit: 'Copy',
        error: 'The copy was refused'
      },
      taskStarted: 'Started — it continues as a background task',
      cephObjects: 'Ceph object details',
      phase: {
        ready: 'Ready',
        creating: 'Creating',
        'awaiting-upload': 'Awaiting upload',
        downloading: 'Downloading',
        verifying: 'Verifying',
        deleting: 'Deleting',
        error: 'Error',
        'in-use': 'In use'
      }
    }
  }
}

export const itIT: typeof enUS = {
  storage: {
    pools: {
      title: 'Storage',
      createTitle: 'Crea storage',
      path: 'Percorso',
      pathHint: 'Percorso assoluto sotto una root consentita (default /var/lib/tvirt/pools/)',
      shared: 'Storage condiviso',
      sharedChip: 'condiviso',
      sharedVerifiedChip: 'condiviso · verificato',
      sharedVerifiedTooltip: 'Condivisione verificata: le sonde incrociate confermano che ogni membro vede i dati scritti dagli altri.',
      sharedDisputedChip: 'condiviso · NON verificato',
      sharedDisputedTooltip: 'Condivisione NON verificata',
      sharedDisputedPair: '{observer} non vede i dati scritti da {writer}',
      sharedHint: 'Share di rete montata localmente allo stesso path su ogni membro — prerequisito della live migration (dichiarativo).',
      sharedDanger: "PERICOLO — TVirt CREDE a questo flag: accendendolo ogni membro assume di vedere GLI STESSI byte a questo path. Su una cartella che NON è una share davvero condivisa (NFS/CIFS/filesystem di cluster) il risultato è corruzione e perdita di dati alla prima migrazione o al primo riavvio HA su un altro nodo. Accendilo solo se il path è una share montata in modo identico su ogni nodo.",
      type: 'Tipo',
      capacity: 'Capacità',
      provisioned: 'Allocata',
      provisionedOvercommit: 'Impegno potenziale oltre la capacità: i volumi thin possono crescere fino a {n} GiB.',
      phase: { ready: 'Pronto', degraded: 'Degradato', error: 'Errore' },

      // Round #156 (dialog standard): il dialog dettagli COMPLETO — tab,
      // le righe di config per-driver e la superficie osservata.
      tabConfig: 'Configurazione',
      tabCapacity: 'Capacità e stato',
      cardIdentity: 'Identità',
      cardPlacement: 'Collocazione',
      cardConnection: 'Connessione',
      usedReal: 'Occupazione reale',
      capacityUnknown: 'Il nodo non ha ancora riportato la capacità di questo pool — qui non si inventano numeri',
      overcommitHint: 'Impegno oltre la capacità: legittimo sui pool thin, tieni d\'occhio l\'occupazione reale',
      supportedContent: 'Contenuti supportati',
      content: {
        disks: 'Dischi',
        catalogFiles: 'File di catalogo',
        backups: 'Backup',
        appVolumes: 'Volumi App'
      },
      defaultDiskFormat: 'Formato disco predefinito',
      reserveGiB: 'Spazio libero riservato (GiB)',
      available: 'Disponibile',
      diskCount: 'Dischi',
      filesystem: 'Filesystem',
      observedAt: 'Osservato il',
      lostAtRow: 'Dichiarato perduto il',
      sharedProbeRow: 'Verifica condivisione',
      sharedProbeNodes: 'Nodi sondati',
      reachableFrom: 'Raggiungibilità dichiarata',
      reachableFromHint: 'I nodi da cui questo storage deve essere raggiungibile. Una VM con dischi, ISO o metadati qui sopra non parte né migra su un nodo fuori da questa lista. Tutti i membri sono preselezionati: restringi come vuoi. Un nodo che entra nel cluster in seguito resta fuori finché non lo aggiungi qui.',
      reachableFromNone: 'non dichiarata',
      reachableFromTags: 'Raggiungibilità per tag',
      reachableFromTagsHint: 'Selettore tag=valore (voci separate da virgola, in AND): ogni membro che porta questi tag è dichiarato raggiungibile — dinamicamente: taggare un nodo lo fa entrare.',
      reachableFromTagsInvalid: 'Formato non valido — usa tag=valore, separati da virgola',
      reachableResolved: 'Nodi risolti: {nodes}',
      reachableDeclaredNotSeen: 'dichiarato ma non visto dalla sonda di condivisione su: {nodes}',
      reachableSeenNotDeclared: 'visto ma non dichiarato (fuori perimetro) su: {nodes}',
      cephCluster: 'Cluster Ceph gestito',
      appGuardTitle: 'Storage in uso dalle App',
      appEphemeralGuard: 'questo storage è il pool effimero di cluster delle App — cambialo prima dalla pagina App, poi eliminalo.',
      appInUseGuard: 'qui vivono root/volumi di app: {apps} — spostali o eliminali prima.',

      // #355 — il rifiuto `release_required` della delete: il pool OSPITA
      // immagini OCI materializzate, e la conferma le fa tornare non
      // materializzate senza cancellare un byte (idioma `purge_required`).
      releaseRequiredTitle: "Eliminare '{name}' e rilasciare le sue immagini?",
      releaseRequiredBatchTitle: 'Eliminare gli storage selezionati e rilasciare le loro immagini?',
      releaseRequiredBody: 'Rilasciare le immagini non cancella NESSUN byte: la de-registrazione lascia il namespace su disco, le immagini tornano semplicemente non materializzate e ogni riferimento degli scope sopravvive come claim — ri-registrando il pool vengono riadottate.',
      releaseConfirm: 'Elimina e rilascia le immagini'
    },
    lost: {
      chip: 'Perduto',
      action: 'Dichiara perduto',
      confirmTitle: 'Dichiara lo storage perduto',
      confirmBody: 'Attestazione dell\'operatore a proprio rischio dichiarato: ogni task in coda o in corso su questo storage o sui suoi oggetti FALLIRÀ, le eliminazioni diventano solo a registro (nessun lavoro su filesystem) e il nuovo lavoro fisico viene rifiutato. I byte non vengono mai toccati — se il volume torna in vita, registralo di nuovo ed esegui uno scan per ri-adottare tutto.',
      confirmSubmit: 'Dichiara perduto',
      error: 'L\'attestazione è stata rifiutata'
    },
    findings: {
      action: 'Oggetti trovati',
      title: 'Oggetti trovati',
      empty: 'Nessun oggetto trovato nel namespace dello storage',
      loadError: 'Impossibile caricare gli oggetti trovati',
      actionError: 'La decisione è stata rifiutata',
      colKind: 'Tipo',
      colObject: 'Oggetto',
      colConflict: 'Conflitto',
      namespaceNotice:
        'Questa directory era già inizializzata da un altro storage: il sigillo di identità è stato riscritto con quello di questo storage. I contenuti NON sono stati toccati — guarda gli oggetti trovati qui sotto e importa quelli che ti servono, poi ignora questo avviso.',
      kind: {
        disk: 'Disco',
        catalogFile: 'File di catalogo',
        vmMetadata: 'Metadata VM',
        namespace: 'Namespace',
        foreign: 'Estraneo',
        volumeGroup: 'Volume group'
      },
      vgAdopt: {
        action: 'Adotta il volume group',
        title: 'Adotta il volume group',
        intro:
          'Su questo dispositivo esiste già un volume group che porta un nome di TVirt, ma il suo sigillo non nomina nessuno storage registrato qui. Può essere ciò che un precedente storage di QUESTO cluster ha lasciato sulla LUN — oppure ciò che un ALTRO cluster TVirt ha lasciato su una LUN passata di mano: da qui i due casi sono identici, ed è per questo che nessuno decide al posto tuo.',
        irreversible: 'Guarda il dispositivo, il sigillo e il nome prima di procedere: l\'adozione non si annulla.',
        device: 'Dispositivo',
        seal: 'Sigillo',
        sealNone: 'nessuno (nessun tag tvirt.storageId)',
        renameTo: 'Verrà rinominato in',
        renameNone: 'Nessuna rinomina: il nome è già quello che questo storage deriva',
        volumesNote:
          'I volumi contenuti NON vengono adottati da questo gesto: il prossimo scan li offre uno per uno, come oggetti trovati ordinari.'
      },
      conflictChip: 'ID già esistente',
      dismissedChip: 'Ignorato',
      adopt: 'Adotta',
      adoptFileTitle: 'Importa in un catalogo',
      adoptFileHint: 'I file esistono solo dentro i cataloghi: scegli il catalogo di destinazione per il file trovato.',
      purge: 'Elimina dal disco',
      dismiss: 'Ignora',
      purgeConfirmTitle: 'Elimina dal disco',
      purgeConfirmBody: 'I byte trovati di {id} verranno rimossi dallo storage. L\'operazione non è reversibile.',
      taskStarted: 'Avviato — prosegue come task in background',
      scan: 'Scansiona lo storage',
      scanStarted: 'Scansione avviata…',
      scanDone: 'Scansione conclusa — gli oggetti trovati sono aggiornati'
    },
    reinit: {
      action: 'Riprova inizializzazione'
    },
    wizard: {
      addButton: 'Aggiungi',
      title: 'Aggiungi storage',
      back: 'Indietro',
      continue: 'Continua',
      selectHint: 'Seleziona un protocollo per vedere come TVirt lo usa.',
      roadmap: 'Roadmap',
      server: 'Server',
      export: 'Percorso esportato',
      share: 'Share',
      username: 'Nome utente',
      password: 'Password',
      passwordHint: 'Solo scrittura: usata per il mount, mai più mostrata.',
      credential: 'Credenziale',
      credentialManual: 'Inserisci manualmente',
      credentialHint: "Una voce del keystore di tipo username e password — l'alternativa a digitarli qui.",
      credentialEmpty: "Nessuna credenziale di tipo username e password nel keystore — creala in Sistema → Credenziali, oppure digita l'account qui sotto.",
      options: 'Opzioni di mount',
      optionsHint: 'Separate da virgola, passate a mount -o così come sono.',
      portal: 'Portal',
      portalHint: 'host[:porta] — la porta predefinita è 3260.',
      target: 'Target IQN',
      targetHint: 'Il nome del target iSCSI, inizia con iqn.',
      lun: 'LUN',
      wholeLunRow: 'Sui dischi: questo storage nomina solo il target',
      wholeLunHint: 'Questo storage nomina solo il target: le sue LUN diventano dischi una per una (Crea disco → adotta una LUN intera), sul target non si scrive nulla.',
      lunHint: 'Numero della LUN sul target — 0 nel dubbio.',
      chapCredential: 'Credenziale (CHAP)',
      chapCredentialHint: 'Una voce del keystore di tipo CHAP (iSCSI) usata per il login — il segreto non viaggia mai con lo storage.',
      chapNone: 'Nessuna autenticazione',
      chapEmpty: 'Nessuna credenziale di tipo CHAP (iSCSI) nel keystore — creala in Sistema → Credenziali. Senza, il login resta non autenticato.',
      address: 'Indirizzo',
      addressHint: 'Host o IP del target NVMe/TCP (traddr).',
      port: 'Porta',
      portHint: 'Porta TCP del target — 4420 se lasciata vuota.',
      targetNqn: 'Target NQN',
      targetNqnHint: 'Lo NQN del subsystem sul target, inizia con nqn.',
      nsid: 'NSID',
      nsidHint: 'Namespace ID dentro il subsystem — 1 nel dubbio.',
      dhchapCredential: 'Credenziale (DH-HMAC-CHAP)',
      dhchapCredentialHint: 'Una voce del keystore di tipo DH-HMAC-CHAP (NVMe) usata alla connessione — il segreto non viaggia mai con lo storage.',
      dhchapNone: 'Nessuna autenticazione',
      dhchapEmpty: 'Nessuna credenziale di tipo DH-HMAC-CHAP (NVMe) nel keystore — creala in Sistema → Credenziali. Senza, la connessione resta non autenticata.',
      monitors: 'Monitor',
      monitorsHint: 'Endpoint dei monitor Ceph, host[:porta], separati da virgola o a capo — porta predefinita 6789.',
      pool: 'Pool',
      poolHint: 'Il pool RBD da consumare — una immagine RBD per disco.',
      fs: 'Filesystem',
      fsHint: 'Il nome del filesystem CephFS (mds_namespace al mount).',
      fsPath: 'Path dentro il filesystem',
      fsPathHint: 'Path assoluto dentro il filesystem che lo storage monta — / se in dubbio.',
      cephxCredential: 'Credenziale (cephx)',
      cephxCredentialHint: 'Una voce del keystore di tipo cephx: username = nome client (client.…), secret = la chiave base64. Obbligatoria.',
      cephxNone: 'Nessuna credenziale cephx nel keystore — creane una (tipo: cephx) in Sistema → Credenziali, oppure lascia che un cluster Ceph gestito la coni con "Proponi come storage".',
      sharedByNature: 'Gli storage di rete sono condivisi per natura: ogni membro li monta allo stesso percorso gestito da TVirt.',
      error: 'Lo storage è stato rifiutato',
      category: {
        nas: 'NAS',
        san: 'SAN',
        ceph: 'Ceph',
        other: 'Altro',
        legacy: 'Legacy'
      },
      legacyHint: 'Supportati a tutti gli effetti — semplicemente non li sceglieremmo per una installazione nuova.',
      proto: {
        // La riga `usage` (Luca 2026-07-24) dice come TVirt usa il protocollo —
        // in primis i contenuti ammessi: dir/nfs/cifs ospitano dischi E immagini
        // ISO/disco e template, i driver a blocchi (iscsi/nvme-tcp/rbd) SOLO
        // dischi.
        dir: {
          name: 'Cartella locale',
          desc: 'Una directory del nodo: semplice e veloce. È LOCALE a un nodo, a meno che il path non sia una share davvero condivisa — marcarla condivisa quando non lo è costa dati.',
          usage: 'TVirt ci conserva dischi delle VM, immagini ISO/disco e template.'
        },
        nfs: {
          name: 'NFS',
          desc: 'Un export NFS montato dal cluster — condiviso per natura.',
          usage: 'Ospita dischi delle VM, immagini ISO/disco e template.'
        },
        cifs: {
          name: 'CIFS/SMB',
          desc: 'Una share Windows/Samba montata dal cluster — condivisa per natura.',
          usage: 'Ospita dischi delle VM, immagini ISO/disco e template.'
        },
        sftp: {
          name: 'SFTP',
          desc: 'Un filesystem remoto via SSH — senza bisogno di export NAS.',
          usage: 'In roadmap.'
        },
        iscsi: {
          name: 'iSCSI',
          desc: 'LUN a blocchi via IP con LVM condiviso sopra.',
          usage: 'Solo dischi — un volume thin per disco; non ospita immagini ISO né template.'
        },
        rbd: {
          name: 'Ceph RBD',
          desc: 'Block device distribuiti su un cluster Ceph.',
          usage: "Solo dischi — un'immagine RBD per disco, aperta nativamente da QEMU; non ospita immagini ISO."
        },
        cephfs: {
          name: 'CephFS',
          desc: 'Il filesystem distribuito di Ceph, montato dal kernel sul cluster — shared by nature.',
          usage: 'Ospita dischi VM, immagini ISO/disco e template — la famiglia dir su un filesystem Ceph (credenziale cephx obbligatoria).'
        },
        fc: { name: 'SCSI/FC', desc: 'LUN SCSI su fabric FibreChannel — la SAN FC classica.' },
        fcoe: { name: 'SCSI/FCoE', desc: 'Lo stesso SCSI su FibreChannel, con i frame FC incapsulati in Ethernet.' },

        // Vivo dal round #132 — la chiave è il nome del driver del contract,
        // così il chip della lista storage risolve la stessa voce.
        // Round #173 (T173-P10): the whole-LUN carriers — the key is the contract
        // driver name, so the storage-list chip resolves the same entry.
        'iscsi-lun': {
          name: 'iSCSI (LUN intere)',
          desc: 'Il target iSCSI come portatore di LUN intere: ogni disco È una LUN, passata cruda al guest — niente LVM in mezzo.',
          usage: 'Solo dischi — una LUN intera per disco, adottata com\'è; la casa SAN dei dischi a scrittura condivisa.'
        },
        'nvme-tcp-lun': {
          name: 'NVMe/TCP (namespace interi)',
          desc: 'Il subsystem NVMe come portatore di namespace interi: ogni disco È un namespace, passato crudo al guest.',
          usage: 'Solo dischi — un namespace intero per disco, adottato com\'è; scrittura condivisa sì, persistent reservation SCSI no.'
        },
        'nvme-tcp': {
          name: 'NVMe/TCP',
          desc: 'NVMe remoto — nessuno SCSI nel percorso — su TCP semplice, con LVM condiviso sopra.',
          usage: 'Solo dischi — un volume thin per disco; non ospita immagini ISO né template.'
        },
        nvmefc: { name: 'NVMe/FC', desc: 'NVMe remoto su fabric FibreChannel.' },
        nvmefcoe: { name: 'NVMe/FCoE', desc: 'NVMe sul fabric FibreChannel che FCoE trasporta dentro Ethernet — i layer FCoE e NVMe/FC impilati.' },
        iser: { name: 'iSCSI/RDMA (iSER)', desc: 'iSCSI col percorso dati spostato su RDMA — qualsiasi fabric (InfiniBand, iWARP, RoCE). Superato da NVMe/RDMA, ancora presente negli array e in ESXi.' },
        srp: { name: 'SCSI/RDMA (SRP)', desc: "SCSI nativo su RDMA, l'era pre-iSER: InfiniBand (col discovery di fabric) e RoCE — sopravvive negli storage HPC datati." },
        nvmerdma: { name: 'NVMe/RDMA', desc: 'NVMe remoto su qualsiasi fabric RDMA — InfiniBand, iWARP, RoCE v1 e v2: una sola configurazione, è il nodo a dire quale ha.' },
        s3: { name: 'S3', desc: 'Object storage compatibile S3 — immagini e backup.' }
      }
    },
    catalogs: {
      title: 'Cataloghi',
      createTitle: 'Crea catalogo',
      editTitle: 'Modifica catalogo',
      docTitle: 'Catalogo',
      notFound: 'Questo catalogo non esiste (più?)',
      scopeHint: 'Chi legge questo scope vede (e può usare) ogni file del catalogo',
      files: 'File',
      noFiles: 'Nessun file',
      count: {
        iso: 'ISO',
        ctRootfs: 'Tpl CT',
        vmTemplate: 'Tpl VM',
        ociImage: 'OCI',
        firmwareOvmf: 'Firmware'
      },

      // Il catalogo di sistema (round #126): la casa degli oggetti integrati
      // della piattaforma — leggibile da tutti, scrivibile da nessuno.
      systemChip: 'di sistema',
      systemTooltip: 'Catalogo di sistema: ospita gli oggetti integrati della piattaforma e non si può modificare'
    },

    // File di catalogo (round K1, inventory v2e/v2g): il catalogo è la casa
    // degli artefatti file del cluster — una lista, titoli per kind.
    catalogFiles: {
      catalog: 'Catalogo',
      kindLabel: 'Tipo',

      // #264: le due dimensioni di un file a forma di disco — i byte occupati
      // sullo storage e la capacità vista dal guest (pacchetti multi-disco:
      // la somma dei dischi membri).
      physicalSize: 'Dimensione fisica',
      virtualSize: 'Dimensione virtuale',

      // Round K3 (#64) + K4 (#65, ova/ovf con la mappatura boot-survival):
      // materializza un file vm-template come VM Template dell'inventario.
      importTemplateAction: 'Importa come VM Template',
      importTemplateLegacy: 'Nessun formato dichiarato — usabile come seed per dischi, non importabile',
      importTemplateTitle: 'Importa come VM Template',
      importTemplateSubmit: 'Importa',
      importTemplateError: 'Import non riuscito',
      importTemplateHint: "Il pacchetto .tvtpl diventa un VM Template: l'hardware congelato del manifest più un disco per entry dell'archivio, ognuno verificato contro il suo checksum. NIC e supporti CD/DVD non fanno parte di un archivio — aggiungili dopo l'import.",
      importTemplateQcow2Hint: 'Un qcow2 nudo diventa un template a UN disco con l’hardware di default dichiarato: profilo linux, UEFI, 2 vCPU, 2048 MiB, boot dal suo unico disco. Correggi al deploy.',
      importTemplateName: 'Nome del template',
      importTemplateNameHint: 'Vuoto = {name} (-import-<n> se occupato)',
      importTemplateStorage: 'Storage per i dischi',
      importTemplateMetadataStorage: 'Storage metadati',
      importTemplateMetadataHint: 'Uno storage a blocchi non ospita i metadati VM (firmware, varstore) — scegline uno dir/nfs/cifs.',
      importTemplateStarted: 'Import avviato — il template compare nel tab Template alla chiusura del task.',

      // Etichette parlanti al singolare (#86): findings e censimento dicono
      // COSA è il file — il generico 'File di catalogo' resta solo fallback.
      kind: {
        iso: 'Immagine ISO',
        'ct-rootfs': 'Rootfs CT',
        'vm-template': 'Template VM',
        'oci-image': 'Immagine OCI',
        'firmware-ovmf': 'Firmware OVMF'
      },
      title: {
        iso: 'Immagini ISO',
        'ct-rootfs': 'Template CT',
        'vm-template': 'Template VM',
        'oci-image': 'Immagini OCI',
        'firmware-ovmf': 'Firmware OVMF'
      },
      createTitle: {
        iso: 'Aggiungi immagine ISO',
        'ct-rootfs': 'Registra template CT',
        'vm-template': 'Aggiungi template VM',
        'oci-image': 'Aggiungi immagine OCI',
        'firmware-ovmf': 'Aggiungi firmware OVMF'
      },
      formatHint: 'Derivato dall\'estensione del file scelto — correggilo se ha indovinato male',
      tarballHint: 'Un tarball rootfs nel formato LXC standard (.tar.gz / .tar.xz / .tar.zst / .tar.bz2, o un .tar semplice)',
      ociArchiveHint: 'Un tarball docker-archive / oci-archive (.tar o .tar.gz), ad esempio da docker save',
      uploadResumeHint: 'Upload interrotto — il file è in attesa di upload: invia di nuovo con lo stesso file per riprendere',
      moveTitle: 'Sposta file',
      moveHint: 'Due dimensioni indipendenti: il Catalogo sposta il collegamento (stesso id, nessun byte si muove, lo scope diventa quello del catalogo di arrivo); lo Storage sposta i dati — ogni copia collegata lo segue, rifiutato mentre il file è in uso. Scelti insieme, prima il catalogo poi lo storage.',
      moveCatalog: 'Catalogo di destinazione',
      moveCatalogHint: 'Solo il collegamento cambia casa: una VM che monta il file deve poter vedere il catalogo di arrivo',
      keepCurrent: 'Non cambiare',
      rehomed: 'Collegamento spostato nel catalogo scelto',
      copyAction: 'Copia in catalogo…',
      copyTitle: 'Copia in catalogo',
      copyLinkHint: 'La copia è un nuovo collegamento agli stessi dati — nulla viene duplicato sullo storage.',
      copyCatalog: 'Catalogo di destinazione',
      copyCatalogHint: 'La copia è il modo in cui un file viaggia tra cataloghi',
      sameCatalog: 'Lo stesso della sorgente',
      sharedObjectHint: 'I dati di questo file sono condivisi da {count} copie collegate',
      deleteLastLink: 'Questo è l\'ULTIMO collegamento a questo contenuto: verranno eliminati anche i dati dallo storage.',
      deleteLinkOnly: 'Verrà rimosso solo questo collegamento — i dati restano, ancora raggiungibili dalle altre copie collegate ({count}).',
      noCatalog: 'Non c\'è alcun catalogo — creane uno',
      inlineCatalogName: 'Nome del catalogo',

      // File integrati nella piattaforma (round #126): storage null — si
      // leggono e si clonano, mai move/delete/rename.
      embeddedChip: 'integrato',
      embeddedStorage: 'Integrato nella piattaforma',
      embeddedNoMove: 'File integrato della piattaforma: non vive su alcuno storage — clonalo invece',
      embeddedCopyHint: 'Il clone materializza i byte integrati su uno storage vero: scegli storage e catalogo di destinazione — la copia è un file normale',
      sourceEmbedded: 'Integrato nella piattaforma',

      // Round #142 (T142-P4): the per-node matrix of an embedded file —
      // where the asset is (and which version), where it is missing.
      nodesRow: 'Presenza per nodo',
      nodesSummary: '{present} nodi su {total} lo hanno',
      nodePresent: 'presente',
      nodeMissing: 'assente',
      nodeUnknown: 'non ancora riportato',
      nodesHint: 'L\'asset vive sul disco di ogni nodo (pacchetto trantor-tvirt-assets): un nodo che non lo ha non può definire una VM con questo firmware né servire le Guest Tools. Versioni diverse fra i nodi sono normali durante un aggiornamento a rotazione.',

      // Download (round #108): i byte viaggiano col loro permesso dedicato.
      downloadAction: 'Scarica',
      downloadNotReady: 'Il contenuto non è ancora pronto — il download richiede un file ready',
      downloadDenied: 'Il tuo ruolo non include catalog.download — scaricare i byte è un diritto separato dall\'uso del file',

      // Round #156 (dialog standard): le righe di lettura mancanti del dialog
      // dettagli — i fatti dell'oggetto condiviso, il checksum dichiarato, gli
      // utilizzatori.
      objectRow: 'Oggetto storage',
      objectLinksRow: 'Copie collegate',
      declaredChecksum: 'SHA-256 dichiarato',
      usedByRow: 'Usato da',
      usedByVms: 'VM',
      usedByDisks: 'Dischi',
      transferRow: 'Trasferimento',
      storageLockHint: 'Cambia solo con l\'azione Sposta'
    },

    // Il censimento per-storage (round K1, inventory v2b #2): sola lettura,
    // ogni oggetto si gestisce dalla sua sezione.
    objects: {
      action: 'Esplora contenuti',
      title: 'Contenuti dello storage',
      hint: 'Censimento in sola lettura di cosa vive su questo storage — ogni oggetto si gestisce dalla sua sezione.',
      empty: 'Nessun oggetto registrato su questo storage',
      loadError: 'Impossibile caricare i contenuti',
      colDetail: 'Dettaglio',
      manage: 'Gestisci nella sua sezione',
      kind: {
        disk: 'Disco',
        catalogFile: 'File di catalogo',
        ociBlob: 'Blob OCI',
        checkpoint: 'Checkpoint',
        vmMetadata: 'Metadata VM',
        appRoot: 'Root App',
        appVolume: 'Volume App'
      }
    },
    inventory: {
      tabCatalogs: 'Cataloghi',
      storage: 'Storage',
      sizeGib: 'Dimensione (GiB)',
      format: 'Formato',
      size: 'Dimensione',
      sourceType: 'Sorgente',
      sourceFile: 'Caricamento file',
      sourceUrl: 'Download da URL',
      file: 'File',
      url: 'URL',
      checksum: 'Checksum SHA-256',
      checksumHint: 'Obbligatorio: il download viene verificato contro questo SHA-256',
      verifyChecksum: 'Verifica checksum',
      checksumOffHint: 'Il download sarà accettato senza verifica — lo SHA-256 calcolato viene comunque registrato',
      uploading: 'Caricamento…',
      move: {
        action: 'Sposta',
        diskTitle: 'Sposta disco',
        target: 'Storage di destinazione',
        diskHint: 'Il disco conserva la sua identità: i byte viaggiano nello storage scelto. Una VM che lo usa deve essere spenta; con snapshot presenti un lato iSCSI rifiuta lo spostamento.',
        noTargets: 'Nessuno storage di destinazione compatibile',
        submit: 'Sposta',
        error: 'Lo spostamento è stato rifiutato'
      },
      clone: {
        action: 'Clona',
        title: 'Clona disco',
        name: 'Nome della copia',
        storage: 'Storage di destinazione',
        sameStorage: 'Stesso della sorgente',
        linked: 'Linked clone',
        linkedHint: 'Overlay qcow2 istantaneo incatenato al disco sorgente (solo sorgenti qcow2). Il default è una copia completa.',
        linkedCrossHint: 'Un linked clone resta sullo storage della sorgente: scegli "Stesso della sorgente" per abilitarlo.',
        submit: 'Clona',
        error: 'La clonazione è stata rifiutata'
      },
      copy: {
        action: 'Copia',
        name: 'Nome della copia',
        storage: 'Storage di destinazione',
        sameStorage: 'Stesso della sorgente',
        submit: 'Copia',
        error: 'La copia è stata rifiutata'
      },
      taskStarted: 'Avviato — prosegue come task in background',
      cephObjects: 'Vai ai dettagli oggetto su Ceph',
      phase: {
        ready: 'Pronta',
        creating: 'In creazione',
        'awaiting-upload': 'In attesa di upload',
        downloading: 'In download',
        verifying: 'In verifica',
        deleting: 'In eliminazione',
        error: 'Errore',
        'in-use': 'In uso'
      }
    }
  }
}
